import { useMemo, useState, useRef, useEffect } from "react";
import PreviewModal from "../components/PreviewModal";
import LogoLeiste from "../components/LogoLeiste";
import SignupForm from "../components/SignupForm";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import { TbChevronDown } from "react-icons/tb";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { alpha } from "@mui/material/styles";


import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

export default function Home() {
  const [showPreview, setShowPreview] = useState(false);
  const baseUrl = import.meta.env.BASE_URL;

  const videoTrackRef = useRef(null);
  const [videoCanLeft, setVideoCanLeft] = useState(false);
  const [videoCanRight, setVideoCanRight] = useState(true);

  const updateVideoEdges = () => {
    const el = videoTrackRef.current;
    if (!el) return;

    const eps = 12; // ✅ größer wegen scroll-snap + subpixel
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);

    setVideoCanLeft(el.scrollLeft > eps);
    setVideoCanRight(el.scrollLeft < maxLeft - eps);
  };

  useEffect(() => {
    const el = videoTrackRef.current;
    if (!el) return;

    // ✅ mehrfach nachm Layout/Images checken
    requestAnimationFrame(updateVideoEdges);
    setTimeout(updateVideoEdges, 60);
    setTimeout(updateVideoEdges, 250);

    const onScroll = () => updateVideoEdges();
    el.addEventListener("scroll", onScroll, { passive: true });

    const onResize = () => updateVideoEdges();
    window.addEventListener("resize", onResize);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);


  const MEDIAKIT_LESSONS = useMemo(
    () => [
      {
        id: "m1",
        img: "videos/v1.png",
        length: "05:30",
        title: "Was ist ein Mediakit?",
        desc: "Wofür ein Mediakit wirklich da ist und warum es dir schneller zu Gigs verhilft.",
      },
      {
        id: "m2",
        img: "videos/v2.png",
        length: "04:00",
        title: "Künstlername & Tagline",
        desc: "So formulierst du Name und Tagline, damit Veranstalter:innen sofort verstehen, was du anbietest.",
      },
      {
        id: "m3",
        img: "videos/v3.png",
        length: "04:20",
        title: "Eine Bio, die gelesen wird",
        desc: "Kurz, klar, glaubwürdig: Aufbau, Tonalität und die wichtigsten Inhalte.",
      },
      {
        id: "m4",
        img: "videos/v4.png",
        length: "04:00",
        title: "Repertoire & Angebot",
        desc: "Was spielst du, für wen, in welchen Formaten? So präsentierst du Sets, Dauer, Besetzung und Anlässe.",
      },
      {
        id: "m5",
        img: "videos/v5.png",
        length: "03:55",
        title: "Die richtigen Fotos",
        desc: "Welche Motive du brauchst, welche Fehler du vermeidest und wie du sofort hochwertiger wirkst.",
      },
      {
        id: "m6",
        img: "videos/v6.png",
        length: "02:40",
        title: "Techrider verständlich machen",
        desc: "Technik-Anforderungen so formulieren, dass Locations schnell checken, was gebraucht wird.",
      },
      {
        id: "m7",
        img: "videos/v7.png",
        length: "03:30",
        title: "Call-to-Action, der zu Buchungen führt",
        desc: "Wie du Kontakt, nächste Schritte und Buchungsinfos so platzierst, dass Menschen wirklich reagieren.",
      },
      {
        id: "m8",
        img: "videos/v8.png",
        length: "0130",
        title: "Design: Farben, Layout & Feinschliff",
        desc: "So wirkt dein Mediakit konsistent, modern und professionell, ohne Designer:in sein zu müssen.",
      },
    ],
    []
  );

  const scrollToMediakit = () => {
    const el = document.getElementById("mediakit-title");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const MiniBenefit = ({ strong, label }) => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <CardContent sx={{ p: { xs: 1.6, md: 1.8 } }}>
        <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>{strong}</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{label}</Typography>
      </CardContent>
    </Card>
  );

  const SoftCard = ({ children, sx }) => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Card>
  );

  const BridgeArrow = () => (
    <Box
      aria-hidden="true"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 60 70"
        sx={{ width: 54, height: 64, display: "block" }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
        </defs>
        <polygon points="55,35 5,5 5,65" fill="url(#grad1)" opacity="0.98" />
      </Box>
    </Box>
  );

  return (
    <Box component="main">

      {/* HERO */}
      <Box
        component="section"
        aria-label="Mediakit Generator – Hero"
        className="sectionBg sectionA"
        sx={(theme) => ({
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: "90vh", md: "60vh" },
          display: "flex",
          alignItems: "center",
          py: { xs: 5, md: 8 },
          background:
            theme.palette.mode === "dark"
              ? "radial-gradient(1100px 520px at 12% 0%, rgba(163,216,30,0.22), transparent 56%), radial-gradient(900px 500px at 90% 18%, rgba(23,23,15,0.18), transparent 56%)"
              : "radial-gradient(1100px 520px at 12% 0%, rgba(163,216,30,0.14), transparent 56%), radial-gradient(900px 500px at 90% 18%, rgba(23,23,15,0.10), transparent 56%)",
        })}
      >
        <Container maxWidth="lg" sx={{ width: "100%" }}>
          {/* ✅ statt <Grid>: CSS Grid (stabil) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, // ✅ Desktop nebeneinander
              gap: { xs: 4, md: 10 },
              alignItems: "center",
            }}
          >
            {/* TEXT (links am Desktop, unten am Mobile) */}
            <Box sx={{ order: { xs: 2, md: 1 } }}>
              <Stack spacing={{ xs: 2, md: 2.6 }}>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      letterSpacing: "-0.05em",
                      lineHeight: 1.05,
                      fontSize: { xs: "2.15rem", sm: "2.45rem", md: "3.25rem" },
                    }}
                  >
                    Erstelle DEIN Mediakit
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.8,
                      fontWeight: 900,
                      letterSpacing: "-0.05em",
                      lineHeight: 1.05,
                      fontSize: { xs: "2.15rem", sm: "2.45rem", md: "3.25rem" },
                    }}
                  >
                    und hole dir die Gigs.
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "1.03rem", md: "1.10rem" },
                    maxWidth: 620,
                  }}
                >
                  Du bekommst <strong> Zugang zu unserem Mediakit-Generator </strong> und einen <strong>30-Minuten-Videokurs</strong>, der dir zeigt, wie du ein überzeugendes Mediakit erstellst.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {[
                    { strong: "2 Jahre", label: "Zugang" },
                    { strong: "30 min", label: "Erklärvideos" },
                    { strong: "PDF", label: "Export" },
                  ].map((x) => (
                    <Stack
                      key={x.strong}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 0.25,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 900 }}>
                        {x.strong}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        · {x.label}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1.2} alignItems="center" aria-live="polite">
                  <Stack direction="row" spacing={-0.9} aria-hidden="true">
                    {[1, 2, 3, 4].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          bgcolor: "grey.300",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                    ))}
                  </Stack>
                  <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                    <strong>37 Artists</strong> haben zuletzt den Generator genutzt
                  </Typography>
                </Stack>


                <Box>
                  <SignupForm />
                </Box>


              </Stack>
            </Box>

            {/* MEDIA (rechts am Desktop, oben am Mobile) */}
            <Box sx={{ order: { xs: 1, md: 2 } }}>
              <Box
                role="button"
                tabIndex={0}
                aria-label="Vorschauvideo abspielen"
                onClick={() => setShowPreview(true)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowPreview(true)}
                sx={{
                  width: "100%",
                  maxWidth: { md: 650 },
                  ml: { md: "auto" },

                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  boxShadow: "0 18px 55px rgba(0,0,0,0.10)",
                  cursor: "pointer",
                  outline: "none",
                  position: "relative",
                  aspectRatio: { xs: "16 / 9", md: "1 / 1" },

                  transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
                  "&:hover": {
                    transform: "translateY(-6px) scale(1.01)",
                    boxShadow: "0 28px 82px rgba(0,0,0,0.18)",
                    borderColor: "rgba(163,216,30,0.45)",
                  },
                  "&:focus-visible": {
                    transform: "translateY(-6px) scale(1.01)",
                    boxShadow: "0 28px 82px rgba(0,0,0,0.18)",
                    borderColor: "rgba(163,216,30,0.60)",
                  },
                }}
              >
                <Box
                  component="img"
                  src={baseUrl + "img2.jpeg"}
                  alt="Mediakit Vorschau"
                  loading="eager"
                  onError={(e) => console.log("Hero image failed:", e.currentTarget.src)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "cover",
                  }}
                />

                {/* Glow */}
                <Box
                  className="hero-glow"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "radial-gradient(640px 460px at 20% 20%, rgba(163,216,30,0.22), transparent 55%), radial-gradient(560px 420px at 90% 30%, rgba(23,23,15,0.14), transparent 58%)",
                    opacity: 0,
                    transition: "opacity 220ms ease",
                    pointerEvents: "none",
                  }}
                />

                {/* Play */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 70, md: 82 },
                      height: { xs: 70, md: 82 },
                      borderRadius: "50%",
                      bgcolor: "rgba(0,0,0,0.45)",
                      backdropFilter: "blur(7px)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <PlayArrowRoundedIcon sx={{ color: "common.white", fontSize: 38 }} />
                  </Box>
                </Box>


                <style>{`
      [aria-label="Vorschauvideo abspielen"]:hover .hero-hint,
      [aria-label="Vorschauvideo abspielen"]:focus-visible .hero-hint {
        opacity: 1;
        transform: translateY(0px);
      }
      [aria-label="Vorschauvideo abspielen"]:hover .hero-glow,
      [aria-label="Vorschauvideo abspielen"]:focus-visible .hero-glow {
        opacity: 1;
      }
    `}</style>
              </Box>
            </Box>

          </Box>


        </Container>
      </Box>


      {/* WAS IST EIN MEDIAKIT */}
      <Box component="section" className="sectionBg sectionB" aria-labelledby="mediakit-title" sx={{ py: { xs: 5, md: 10 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: { xs: "block", md: "flex" },
              alignItems: "center",
              gap: { xs: 3, md: 6 },
            }}
          >
            {/* Bild: 1/3 */}
            <Box
              sx={{
                flex: { md: "0 0 35%" },
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                mb: { xs: 2, md: 0 },
              }}
            >
              <Box
                component="img"
                src={baseUrl + "mediakit-sample.png"}
                alt="Beispiel eines Mediakit"
                sx={{
                  width: { xs: "82%", sm: "80%", md: "100%" },
                  maxWidth: { xs: 360, sm: 420, md: "none" },
                  height: "auto",
                  borderRadius: 1,
                  objectFit: "cover",
                }}
              />
            </Box>

            {/* Text: 2/3 */}
            <Box sx={{ flex: { md: "0 0 65%" }, minWidth: 0 }}>
              <Typography
                id="mediakit-title"
                variant="h4"
                sx={{ fontWeight: 900, letterSpacing: "-0.03em" }}
              >
                Was ist ein Mediakit?
              </Typography>

              <Box sx={{ height: 10 }} />

              <Typography sx={{ color: "text.secondary" }}>
                Ein <strong>Mediakit</strong> ist deine digitale Visitenkarte als Musiker:in. Es fasst
                alle wichtigen Infos über dich, deine Musik und deine Auftritte in einem professionellen
                Dokument zusammen: klar, ansprechend und überzeugend. So überzeugst du Veranstalter:innen, Hotels oder Firmen auf den ersten Blick und erhöhst deine Chancen auf neue Auftritte und Kooperationen.
              </Typography>
              <Box sx={{ py: 2 }} />

              {/* VERGLEICH */}

              <Box sx={{ mt: 1.6 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 72px 1fr" },
                    gap: { xs: 0.0, md: 0.5 },
                    alignItems: "stretch",
                  }}
                >
                  {/* OHNE MEDIAKIT */}
                  <Box
                    sx={(t) => ({
                      borderRadius: 1.4,
                      overflow: "hidden",
                      border: `3px solid ${t.palette.mode === "dark" ? "rgba(244,67,54,0.22)" : "rgba(244,67,54,0.12)"
                        }`,
                      background: t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.72)",
                      backdropFilter: "blur(10px)",
                    })}
                  >
                    {/* Header vollflächig + X */}
                    <Box
                      sx={(t) => ({
                        px: { xs: 1.2, md: 1.6 },
                        py: 1.05,
                        background: t.palette.mode === "dark" ? "rgba(244,67,54,0.22)" : "rgba(244,67,54,0.12)",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      })}
                    >
                      <Box
                        sx={(t) => ({
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.18 : 0.12),
                          color: t.palette.error.main,
                        })}
                      >
                        <CancelRoundedIcon sx={{ fontSize: 19 }} />
                      </Box>

                      <Typography sx={{ fontWeight: 950, letterSpacing: 0.2 }}>
                        Ohne Mediakit
                      </Typography>
                    </Box>

                    <Box sx={{ px: { xs: 1.2, md: 1.6 }, py: { xs: 1.1, md: 1.3 } }}>
                      <Stack spacing={1.05} sx={{ mt: 1.1 }}>
                        {[
                          {
                            title: "Mehr Rückfragen & Aufwand",
                            text: "Booker:innen müssen sich Infos zusammensuchen.",
                          },
                          {
                            title: "Wirkt weniger professionell",
                            text: "Ohne Kit fehlt oft Vertrauen & Klarheit.",
                          },
                          {
                            title: "Weniger Buchungschancen",
                            text: "Wenn’s kompliziert ist, wird eher weitergeklickt.",
                          },
                        ].map((item) => (
                          <Box key={item.title} sx={{ display: "flex", gap: 2.1, minWidth: 0 }}>
                            {/* Bullet: nur Kreis (10% smaller look wie bei dir) */}
                            <Box
                              sx={(t) => ({
                                mt: 0.25,
                                width: 12,
                                height: 12,
                                borderRadius: 999,
                                display: "grid",
                                placeItems: "center",
                                flex: "0 0 auto",
                                bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.18 : 0.10),
                                color: t.palette.error.main,
                              })}
                            >
                              <CircleRoundedIcon sx={{ fontSize: 8 }} />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                                {item.title}
                              </Typography>
                              <Typography sx={{ color: "text.secondary", mt: 0.3, lineHeight: 1.5 }}>
                                {item.text}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>

                  {/* CONNECTOR */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        placeItems: "center",
                        transform: { xs: "none", md: "rotate(-90deg)" },
                        opacity: 0.95,
                      }}
                    >
                      <TbChevronDown style={{ fontSize: 75 }} />
                    </Box>
                  </Box>

                  {/* MIT MEDIAKIT */}
                  <Box
                    sx={(t) => ({
                      // Rahmenfarbe = Titel-Hintergrundfarbe
                      borderRadius: 1.4,
                      overflow: "hidden",
                      border: `3px solid ${t.palette.mode === "dark" ? "rgba(76,175,80,0.22)" : "rgba(76,175,80,0.12)"
                        }`,
                      background:
                        t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.72)",
                      backdropFilter: "blur(10px)",
                    })}
                  >
                    {/* Header vollflächig + Haken */}
                    <Box
                      sx={(t) => ({
                        px: { xs: 1.2, md: 1.6 },
                        py: 1.05,
                        background: t.palette.mode === "dark" ? "rgba(76,175,80,0.22)" : "rgba(76,175,80,0.12)",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      })}
                    >
                      <Box
                        sx={(t) => ({
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.18 : 0.12),
                          color: t.palette.success.main,
                        })}
                      >
                        <CheckCircleRoundedIcon sx={{ fontSize: 19 }} />
                      </Box>

                      <Typography sx={{ fontWeight: 950, letterSpacing: 0.2 }}>
                        Mit Mediakit
                      </Typography>
                    </Box>

                    <Box sx={{ px: { xs: 1.2, md: 1.6 }, py: { xs: 1.1, md: 1.3 } }}>
                      <Stack spacing={1.05} sx={{ mt: 1.1 }}>
                        {[
                          {
                            title: "Alles sofort verfügbar",
                            text: "Ein Link/PDF bündelt alle Infos auf einen Blick.",
                          },
                          {
                            title: "Souveräner erster Eindruck",
                            text: "Strukturiert, überzeugend und leicht zu bewerten.",
                          },
                          {
                            title: "Mehr Anfragen & Empfehlungen",
                            text: "Schnell teilbar – ideal für Weiterleitung im Team.",
                          },
                        ].map((item) => (
                          <Box key={item.title} sx={{ display: "flex", gap: 2.1, minWidth: 0 }}>
                            <Box
                              sx={(t) => ({
                                mt: 0.25,
                                width: 12,
                                height: 12,
                                borderRadius: 999,
                                display: "grid",
                                placeItems: "center",
                                flex: "0 0 auto",
                                bgcolor: alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.18 : 0.10),
                                color: t.palette.success.main,
                              })}
                            >
                              <CircleRoundedIcon sx={{ fontSize: 8 }} />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                                {item.title}
                              </Typography>
                              <Typography sx={{ color: "text.secondary", mt: 0.3, lineHeight: 1.5 }}>
                                {item.text}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>

                    </Box>
                  </Box>
                </Box>
              </Box>

            </Box>
          </Box>
        </Container>
      </Box>




      {/* SIRUS – PORTRAIT */}
      <Box component="section" className="sectionBg sectionA" aria-labelledby="mediakit-title" sx={{ py: { xs: 5, md: 10 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: { xs: "block", md: "flex" },
              alignItems: { md: "flex-start" },
              gap: { xs: 3, md: 6 },
            }}
          >
            {/* Text: 2/3 */}
            <Box sx={{ flex: { md: "0 0 70%" }, minWidth: 0 }}>
              {/* Quote + (Mobile) kleines Foto links */}

              <Box
                sx={{
                  display: { xs: "flex", md: "block" },
                  alignItems: "stretch",
                  gap: { xs: 1.2, md: 0 },
                }}
              >
                {/* ✅ Nur Mobile: kleines Portrait links */}
                <Box
                  component="img"
                  src={baseUrl + "sirus-portrait.jpeg"}
                  alt="Sirus – Portrait"
                  sx={{
                    display: { xs: "block", md: "none" },
                    width: 88,
                    height: "auto",
                    alignSelf: "flex-start",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 10px 26px rgba(0,0,0,0.10)",
                  }}
                />

                {/* Quote */}
                <Card
                  elevation={0}
                  sx={{
                    flex: 1, // ✅ damit Card neben dem Foto sauber wächst
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    background: "linear-gradient(135deg, #c3f04c 0%, #a3d81e 100%)",
                    color: "#14150c",
                    boxShadow: "0 14px 40px rgba(0,0,0,0.12)",
                    position: "relative",
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      left: 18,
                      top: -16,
                      fontSize: { xs: 82, md: 102 },
                      opacity: 0.25,
                      fontWeight: 900,
                      lineHeight: 1,
                    }}
                  >
                    “
                  </Box>

                  <CardContent sx={{ p: { xs: 2.5, md: 3 }, pl: { xs: 2.5, md: 7 } }}>
                    <Typography
                      id="sirus-title"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "1.1rem", md: "1.35rem" },
                        lineHeight: 1.3,
                      }}
                    >
                      Ich weiß aus eigener Erfahrung: Talent allein reicht nicht. Wer sich auf der Bühne
                      verkaufen möchte, muss auch das Business dahinter verstehen.
                    </Typography>
                    <Typography sx={{ mt: 1.5, opacity: 0.95 }}>Sirus Madjderey</Typography>
                  </CardContent>
                </Card>
              </Box>

              <Typography
                sx={{
                  mt: 2.5,
                  color: "text.secondary",
                  lineHeight: 1.75,
                  textAlign: "justify",
                  textJustify: "inter-word",

                  // schönerer Blocksatz (modern browsers)
                  hyphens: "auto",
                  WebkitHyphens: "auto",
                  msHyphens: "auto",

                  // verhindert zu große Wortabstände
                  overflowWrap: "break-word",
                  wordBreak: "normal",

                  // optional: wirkt „printiger“
                  letterSpacing: "0.005em",
                }}
              >
                Ich bin Musiker, Autor und Businessmensch. Vor einigen Jahren habe ich ein ungewöhnliches Handwerk
                für mich entdeckt: das Pfeifen. Und klar, du fragst dich: Wer engagiert einen Kunstpfeifer? Genau
                diese Frage habe ich mir am Anfang auch gestellt. Heute halte ich den Weltrekord und war in
                unzähligen TV-, Radio- und Printmedien vertreten. Nicht, weil ich der beste Musiker bin, sondern
                weil ich meine unternehmerische Erfahrung in meine Kunst einfließen ließ, dem Markt zugehört und
                das passende Produkt geschnürt habe. So konnte ich daraus ein Business bauen. Und genau das kannst
                du auch.
              </Typography>


              <Typography variant="h6" sx={{ mt: 2.5, fontWeight: 700 }}>
              </Typography>
            </Box>

            {/* ✅ Großes Bild: nur Desktop anzeigen */}
            <Box
              sx={{
                flex: { md: "0 0 35" },
                display: { xs: "none", md: "flex" }, // <- hier der wichtige Teil
                justifyContent: "flex-end",
                mb: { xs: 1, md: 0 },
              }}
            >
              <Box
                component="img"
                src={baseUrl + "sirus-portrait.jpeg"}
                alt="Sirus – Kunstpfeiffer, Business-Mensch und Bestseller-Autor"
                sx={{
                  width: "100%",
                  height: "auto",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 18px 55px rgba(0,0,0,0.10)",
                }}
              />
            </Box>

          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                columnGap: 1.5,
                mb: 1.2,
                "--g1": "rgba(163,216,30,0.75)",
                "--g2": "rgba(23,23,15,0.70)",
              }}
            >
              {/* links */}
              <Box
                sx={{
                  height: 0.5, // ✅ dünner
                  width: "100%",
                  borderRadius: 999,
                  background: "linear-gradient(90deg, transparent, var(--g1), var(--g2))",
                  opacity: 0.9,
                }}
              />

              {/* text (ein bisschen größer) */}
              <Typography
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.01em",
                  color: "text.secondary",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  fontSize: { xs: 18, md: 18 },
                }}
              >
                Du kennst mich vielleicht aus
              </Typography>

              {/* rechts */}
              <Box
                sx={{
                  height: 0.5, // ✅ dünner
                  width: "100%",
                  borderRadius: 999,
                  background: "linear-gradient(90deg, var(--g2), var(--g1), transparent)",
                  opacity: 0.9,
                }}
              />
            </Box>

            <LogoLeiste />
          </Box>


        </Container>
      </Box>


      {/* INHALTE — kompakter, sicher nebeneinander (ohne MUI Grid-Probleme) */}
      <Box component="section" className="sectionBg sectionB" aria-labelledby="inhalte-title" sx={{ py: { xs: 5, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack spacing={{ xs: 2, md: 2.2 }}>
            {/* GENERATOR — Desktop sicher nebeneinander */}
            <Box
              sx={(t) => ({
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                overflow: "hidden",
                boxShadow: t.palette.mode === "dark"
                  ? "0 18px 44px rgba(0,0,0,0.35)"
                  : "0 18px 44px rgba(0,0,0,0.08)",
              })}
            >
              <Box
                sx={{
                  display: { xs: "block", md: "grid" },
                  gridTemplateColumns: { md: "420px 1fr" }, // Bild-Spalte fix am Desktop
                  alignItems: "stretch",
                }}
              >
                {/* Bild / Preview */}
                <Box
                  sx={(t) => ({
                    position: "relative",
                    borderColor: "divider",
                    bgcolor: t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                  })}
                >
                  <Box
                    component="img"
                    src={baseUrl + "generator-screenshot.jpg"}
                    alt="Mediakit Generator – Vorschau"
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: { xs: 220, sm: 260, md: "100%" }, // ✅ Mobile schöner: oben groß
                      objectFit: "cover",
                      display: "block",
                    }}
                  />

                  {/* optional: softer Glow/Overlay, macht’s “wertiger” */}
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      background:
                        "radial-gradient(520px 240px at 20% 20%, rgba(163,216,30,0.14), transparent 60%), radial-gradient(520px 240px at 85% 30%, rgba(23,23,15,0.10), transparent 60%)",
                      opacity: 0.9,
                    }}
                  />
                </Box>

                {/* Text / Content */}
                <Box sx={{ p: { xs: 2.0, sm: 2.4, md: 2.6 }, minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.15 }}
                  >
                    Der Mediakit-Generator
                  </Typography>

                  <Typography sx={{ color: "text.secondary", mt: 0.8, lineHeight: 1.6 }}>
                    Du trägst nur die wichtigsten Infos ein – wir formen daraus ein sauberes, buchungsfähiges Mediakit
                    im richtigen Format. Fertig zum Teilen als PDF.
                  </Typography>

                  {/* Chips: Mobile zentriert/sauber, Desktop links */}
                  <Box
                    sx={{
                      mt: 1.1,
                      display: { xs: "none", md: "flex" }, // ✅ nur Desktop
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {[
                      { strong: "PDF", label: "Export" },
                      { strong: "Layouts", label: "bewährt" },
                      { strong: "Designer", label: "Vorlagen" },
                      { strong: "Booking", label: "ready" },
                    ].map((x) => (
                      <Stack
                        key={`${x.strong}-${x.label}`}
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                          px: 1,
                          py: 0.25,
                          bgcolor: "action.hover",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 900 }}>
                          {x.strong}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          · {x.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>

                </Box>
              </Box>
            </Box>

            {/* VIDEOKURS — 1 Zeile + Pfeile wie Insta */}
            <Box
              sx={(t) => ({
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                overflow: "hidden", // ✅ wichtig: Fade/Buttons schneiden exakt am Rand
                boxShadow: "0 12px 34px rgba(0,0,0,0.06)",
              })}
            >
              {/* Header-Bereich */}
              <Box sx={{ px: { xs: 1.6, md: 2.2 }, pt: { xs: 1.6, md: 2.0 }, pb: 1.2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Mit Videokurs (8 Lektionen)
                  </Typography>
                </Stack>
              </Box>

              {/* Carousel-Bereich */}
              <Box sx={{ position: "relative" }}>
                {/* ✅ LEFT FADE (bis zum Rand der Box!) */}
                <Box
                  sx={(t) => ({
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: { xs: 76, md: 120 },
                    zIndex: 2,
                    opacity: videoCanLeft ? 1 : 0,
                    transition: "opacity 160ms ease",
                    pointerEvents: "none",
                    background:
                      t.palette.mode === "dark"
                        ? `linear-gradient(90deg, ${alpha("#000", 0.55)} 0%, transparent 100%)`
                        : `linear-gradient(90deg, ${alpha("#fff", 0.95)} 0%, transparent 100%)`,
                  })}
                />

                {/* ✅ RIGHT FADE (bis zum Rand der Box!) */}
                <Box
                  sx={(t) => ({
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: { xs: 76, md: 120 },
                    zIndex: 2,
                    opacity: videoCanRight ? 1 : 0,
                    transition: "opacity 160ms ease",
                    pointerEvents: "none",
                    background:
                      t.palette.mode === "dark"
                        ? `linear-gradient(270deg, ${alpha("#000", 0.55)} 0%, transparent 100%)`
                        : `linear-gradient(270deg, ${alpha("#fff", 0.95)} 0%, transparent 100%)`,
                  })}
                />

                {/* ✅ LEFT BUTTON */}
                <IconButton
                  aria-label="Zurück"
                  onClick={() => {
                    const el = videoTrackRef.current;
                    if (!el) return;
                    el.scrollBy({ left: -Math.round(el.clientWidth * 0.9), behavior: "smooth" });
                  }}
                  sx={(t) => ({
                    position: "absolute",
                    left: { xs: 10, md: 16 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                    opacity: videoCanLeft ? 1 : 0,
                    pointerEvents: videoCanLeft ? "auto" : "none",
                    width: { xs: 48, md: 56 },
                    height: { xs: 48, md: 56 },
                    borderRadius: 999,
                    background:
                      t.palette.mode === "dark"
                        ? "linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.06))"
                        : "linear-gradient(180deg, rgba(255,255,255,1), rgba(255,255,255,0.78))",
                    border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    boxShadow:
                      t.palette.mode === "dark"
                        ? "0 18px 50px rgba(0,0,0,0.65)"
                        : "0 18px 50px rgba(0,0,0,0.22)",
                    backdropFilter: "blur(14px)",
                    transition: "opacity 160ms ease, transform 160ms ease, box-shadow 160ms ease",
                    "&:hover": {
                      transform: "translateY(-50%) scale(1.03)",
                      boxShadow:
                        t.palette.mode === "dark"
                          ? "0 22px 64px rgba(0,0,0,0.72)"
                          : "0 22px 64px rgba(0,0,0,0.28)",
                    },
                  })}
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: 34 }} />
                </IconButton>

                {/* ✅ RIGHT BUTTON */}
                <IconButton
                  aria-label="Weiter"
                  onClick={() => {
                    const el = videoTrackRef.current;
                    if (!el) return;
                    el.scrollBy({ left: Math.round(el.clientWidth * 0.9), behavior: "smooth" });
                  }}
                  sx={(t) => ({
                    position: "absolute",
                    right: { xs: 10, md: 16 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 3,
                    opacity: videoCanRight ? 1 : 0,
                    pointerEvents: videoCanRight ? "auto" : "none",

                    width: { xs: 48, md: 56 },
                    height: { xs: 48, md: 56 },
                    borderRadius: 999,
                    background:
                      t.palette.mode === "dark"
                        ? "linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.06))"
                        : "linear-gradient(180deg, rgba(255,255,255,1), rgba(255,255,255,0.78))",
                    border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    boxShadow:
                      t.palette.mode === "dark"
                        ? "0 18px 50px rgba(0,0,0,0.65)"
                        : "0 18px 50px rgba(0,0,0,0.22)",
                    backdropFilter: "blur(14px)",
                    transition: "opacity 160ms ease, transform 160ms ease, box-shadow 160ms ease",
                    "&:hover": {
                      transform: "translateY(-50%) scale(1.03)",
                      boxShadow:
                        t.palette.mode === "dark"
                          ? "0 22px 64px rgba(0,0,0,0.72)"
                          : "0 22px 64px rgba(0,0,0,0.28)",
                    },
                  })}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 34 }} />
                </IconButton>

                {/* TRACK */}
                <Box
                  ref={videoTrackRef}
                  onScroll={updateVideoEdges}
                  sx={{
                    display: "flex",
                    gap: { xs: 1.2, md: 1.4 },
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    scrollSnapType: "x mandatory",
                    pb: 1.2, // ✅ bisschen Luft unten

                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },

                    // ✅ Mobile eingerückt, Desktop bündig (wie du willst)
                    pl: { xs: 7.0, md: 0 },
                    pr: { xs: 7.0, md: 0 },

                    // ✅ Snap respektiert das Einrücken nur am Mobile
                    scrollPaddingLeft: { xs: 56, md: 0 },
                    scrollPaddingRight: { xs: 56, md: 0 },
                  }}
                >
                  {MEDIAKIT_LESSONS.map((l) => (
                    <Card
                      key={l.id}
                      elevation={0}
                      sx={{
                        flex: "0 0 auto",
                        width: { xs: 240, sm: 280, md: 300 },
                        scrollSnapAlign: "start",
                        borderRadius: 0.5,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        component="img"
                        src={baseUrl + l.img}
                        alt={l.title}
                        loading="lazy"
                        onLoad={updateVideoEdges}
                        sx={{
                          width: "100%",
                          height: { xs: 90, md: 108 },
                          objectFit: "cover",
                          display: "block",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      />

                      <CardContent
                        sx={{
                          p: { xs: 1.05, md: 1.15 },
                          "&:last-child": { pb: { xs: 1.05, md: 1.15 } },
                          flex: 1,
                        }}
                      >
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                            <Typography
                              sx={{
                                fontWeight: 900,
                                lineHeight: 1.2,
                                fontSize: { xs: 13.5, md: 14 },
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {l.title}
                            </Typography>
                            <Typography sx={{ color: "text.secondary", fontSize: 12, flex: "0 0 auto" }}>
                              {l.length}
                            </Typography>
                          </Stack>

                          <Typography
                            sx={{
                              color: "text.secondary",
                              lineHeight: 1.45,
                              fontSize: 12.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {l.desc}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Box>


          </Stack>
        </Container>
      </Box>


      {/* SALES/CTA SECTION – farbig, prominent */}
      <Box
        component="section"
        id="cta-signup"
        className="sectionBg sectionA"
        aria-labelledby="mediakit-cta"
        sx={{ py: { xs: 5, md: 7 } }}
      >
        <Container maxWidth="lg">
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              background: "linear-gradient(135deg, rgba(163,216,30,0.10), rgba(23,23,15,0.08))",
            }}
          >
            <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" },
                  alignItems: "center",
                  gap: { xs: 3, md: 5 },
                }}
              >
                {/* Links: Produktmockup */}
                <Box
                  sx={{
                    flex: { md: "0 0 42%" },
                    display: "flex",
                    justifyContent: { xs: "stretch", md: "flex-start" },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      maxWidth: { xs: "100%", sm: 520, md: "none" },

                      px: { xs: 2.4, sm: 3.0, md: 0 },

                      overflow: "visible",
                    }}
                  >
                    {/* Mockup (links, mobile left-aligned) */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box
                        component="img"
                        src={baseUrl + "mockup-mediakit.png"}
                        alt="Produktmockup: Mediakit-Generator"
                        sx={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                          maxWidth: { xs: 320, sm: 380, md: "100%" },
                        }}
                      />
                    </Box>

                  </Box>
                </Box>






                {/* Rechts: Text */}
                <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
                  <Stack spacing={1.4}>
                    <Typography
                      id="mediakit-cta"
                      variant="h4"
                      sx={{ fontWeight: 900, letterSpacing: "-0.03em" }}
                    >
                      Das Mediakit-Bundle mit interaktivem Generator & Videokurs
                    </Typography>



                    <Typography sx={{ color: "text.secondary" }}>
                      Sichere dir die Kombi zu deinem Musik-Erfolg.
                    </Typography>

                    <List dense sx={{ py: 0, mt: 0.2 }}>
                      {[
                        "Erstelle dein Mediakit ohne Vorkentnisse",
                        "Schritt für Schritt Tutorials",
                        "Direkt im Browser, ohne Software",
                        "Bewährte Layouts für Booking & PR",
                        "PDF-Export",
                      ].map((t) => (
                        <ListItem key={t} sx={{ px: 0, py: 0.2 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckRoundedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={t}
                            primaryTypographyProps={{ sx: { color: "text.secondary" } }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {/* "Jetzt loslegen" entfernen -> nur dein SignupForm */}
                    <SignupForm />
                  </Stack>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>




      {/* Abstand wie <br/><br/> */}
      <Box sx={{ py: 2 }} />

      {/* KURSE */}
      {/*
      <Box id="kurse" component="section" aria-labelledby="kurse-title" sx={{ py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Typography
            id="kurse-title"
            variant="h4"
            sx={{ fontWeight: 900, letterSpacing: "-0.03em" }}
          >
            Lust auf mehr? Schau dir unsere Kurse an!
          </Typography>

          <Box sx={{ height: 16 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 2.2,
            }}
          >
            <SoftCard>
              <Box
                component="img"
                src={baseUrl + "kurse/sales.jpeg"}
                alt="Kursbild: Marketing & Sales"
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: 190, md: 200 },
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <CardContent sx={{ p: 2.2 }}>
                <Stack spacing={1.1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontWeight: 900 }}>Marketing & Sales</Typography>
                    <Typography sx={{ fontWeight: 900 }}>€ 499</Typography>
                  </Stack>
                  <Typography sx={{ color: "text.secondary" }}>
                    7 Stunden • 4 Module • 21 Videos
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    Lerne, wie du dich professionell nach außen präsentierst – online und offline. Von
                    Markenaufbau über Social Media, Pressearbeit und praxisnahen Strategien für Akquise,
                    Sales-Kanäle und den direkten Weg zu mehr Auftritten.
                  </Typography>
                  <Box sx={{ pt: 0.3 }}>
                    <Button
                      component={Link}
                      to="/kurs/marketing-sales"
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: 9, textTransform: "none", fontWeight: 900, py: 1.05 }}
                      onClick={() => posthog.capture("Course clicked", { kurs: "Marketing & Sales" })}
                    >
                      Mehr Infos zum Kurs
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </SoftCard>

            <SoftCard>
              <Box
                component="img"
                src={baseUrl + "kurse/finanzen.jpeg"}
                alt="Kursbild: Finanzen und Steuern"
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: 190, md: 200 },
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <CardContent sx={{ p: 2.2 }}>
                <Stack spacing={1.1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontWeight: 900 }}>Finanzen und Steuern</Typography>
                    <Typography sx={{ fontWeight: 900 }}>€ 499</Typography>
                  </Stack>
                  <Typography sx={{ color: "text.secondary" }}>
                    7 Stunden • 6 Module • 32 Videos
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    Mit dem Wissen der ältesten Steuerkanzlei Wiens lernst du, wie du Steuern optimierst,
                    Förderungen richtig nutzt und deine Gagen professionell kalkulierst.
                  </Typography>
                  <Box sx={{ pt: 0.3 }}>
                    <Button
                      component={Link}
                      to="/kurs/steuern-finanzen"
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.05 }}
                      onClick={() => posthog.capture("Course clicked", { kurs: "Steuern & Finanzen" })}
                    >
                      Mehr Infos zum Kurs
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </SoftCard>

            <SoftCard>
              <Box
                component="img"
                src={baseUrl + "kurse/workflow.jpeg"}
                alt="Kursbild: Zeit, Mindset & Workflow"
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: 190, md: 200 },
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <CardContent sx={{ p: 2.2 }}>
                <Stack spacing={1.1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontWeight: 900 }}>Zeit, Mindset & Workflow</Typography>
                    <Typography sx={{ fontWeight: 900 }}>€ 149</Typography>
                  </Stack>
                  <Typography sx={{ color: "text.secondary" }}>
                    3 Stunden • 3 Module • 12 Videos
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    Mit meinem erprobten Set aus kostenlosen Tools und Zeitmanagement-Techniken bringst du
                    Struktur in deinen Musiker:innen-Alltag und steigerst deine Effizienz.
                  </Typography>
                  <Box sx={{ pt: 0.3 }}>
                    <Button
                      component={Link}
                      to="/kurs/workflow"
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.05 }}
                      onClick={() => posthog.capture("Course clicked", { kurs: "Zeit, Mindset & Workflow" })}
                    >
                      Mehr Infos zum Kurs
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </SoftCard>

            <SoftCard>
              <Box
                component="img"
                src={baseUrl + "kurse/showact.jpeg"}
                alt="Kursbild: Showact & Entertainment"
                loading="lazy"
                sx={{
                  width: "100%",
                  height: { xs: 190, md: 200 },
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <CardContent sx={{ p: 2.2 }}>
                <Stack spacing={1.1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontWeight: 900 }}>Showact & Entertainment</Typography>
                    <Typography sx={{ fontWeight: 900 }}>€ 499</Typography>
                  </Stack>
                  <Typography sx={{ color: "text.secondary" }}>
                    5 Stunden • 4 Module • 21 Videos
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    Lerne, wie du aus deiner Musik eine unvergessliche Show machst – Storyboard,
                    Performance, Lampenfieber, Publikumsbindung und praxisnahe Technik-Einblicke.
                  </Typography>
                  <Box sx={{ pt: 0.3 }}>
                    <Button
                      component={Link}
                      to="/kurs/showkonzept"
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.05 }}
                      onClick={() => posthog.capture("Course clicked", { kurs: "Showact & Entertainment" })}
                    >
                      Mehr Infos zum Kurs
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </SoftCard>
          </Box>

        </Container>
      </Box>
*/}
      <PreviewModal open={showPreview} onClose={() => setShowPreview(false)} />
    </Box>
  );
}
